// Preview of the simplified KareHero colour system.
//
// Mirrors the Figma collections `karehero/colors` (37 variables) and `palette`
// from the new foundations file. Transcribed by hand from the variables panel —
// there is no variables export yet, so if Figma moves, this file must be
// re-synced by hand.
//
// Lives here and NOT in lib/llama/src/theme/mui/color.ts on purpose: the real
// change is a primitives change that lands on every consumer in production.
// This file lets the prototypes be seen in the new palette first, changing
// nothing outside front-end/designs/.
//
// What the simplification does:
//
//  - Only coral, marine green and neutral keep a 10-step ramp. Everything else
//    collapses to a single value.
//  - marine green is RE-INDEXED: the brand #00433D moves from step 700 to 500.
//  - coral is re-cut: only step 500 (#F56B34) survives unchanged, and the
//    ramp now darkens properly to #31150A instead of stalling at #B34C24.
//  - neutral is unchanged except step 700, which becomes charcoal (#404042).
//  - lavender is deleted in Figma. It is KEPT here — see LAVENDER below.
//  - the text group loses its marine. llama paints headings with text/title and
//    sets that to a marine step; here title is charcoal and subtitle is
//    neutral/600, matching the variables panel. Both have to be declared to take
//    effect — see the text block below.

import type { ColorScale } from '@karehero/llama/theme/mui/color';
import { alpha } from '@mui/material/styles';
import type { ThemeOptions } from '@mui/material/styles';

// --- Singles ---------------------------------------------------------------

export const FloralWhite = '#FFFCF5';
export const Gardenia = '#F0EAE0';

/**
 * Gardenia, a step deeper: the same warm beige with the lightness taken down.
 * Stands apart from a gardenia page (1.4:1) and still carries charcoal text at
 * 6.2:1. Onboarding 2.0's reply bubbles. Not in Figma yet.
 */
export const GardeniaDark = '#D7C7AD';
export const Charcoal = '#404042';

export const LimeGreenValue = '#D2E0A3';
export const SunflowerValue = '#FFDD6C';
export const WarmOrangeValue = '#EC9044';
export const CherryValue = '#732F4A';

// A collapsed family exposed as a flat ramp: every step returns the family's
// one value. This is not a shim to dodge the change — it IS the change. The
// family has a single colour now, so `sunflower[400]` and `sunflower[500]`
// SHOULD resolve to the same hex. Keeping the ramp shape means the existing
// `palette.custom.<family>[step]` call sites keep working while the collapse is
// evaluated, instead of silently returning undefined.
const flat = (value: string): ColorScale => ({
  50: value,
  100: value,
  200: value,
  300: value,
  400: value,
  500: value,
  600: value,
  700: value,
  800: value,
  900: value,
});

// --- Ramps that survive ----------------------------------------------------

export const CoralV2: ColorScale = {
  50: '#FDE1D6',
  100: '#FBC4AE',
  200: '#F9A685',
  300: '#F7895D',
  400: '#F67A48',
  500: '#F56B34',
  600: '#C4562A',
  700: '#93401F',
  800: '#622B15',
  900: '#31150A',
};

// 500 is the brand green (#00433D) — labelled `500-base` in Figma. Note this is
// a re-index: in llama today the brand green is step 700.
export const MarineGreenV2: ColorScale = {
  50: '#CCD9D8',
  100: '#99B4B1',
  200: '#668E8B',
  300: '#336964',
  400: '#1A5650',
  500: '#00433D',
  600: '#003631',
  700: '#002825',
  800: '#001B18',
  900: '#000D0C',
};

export const NeutralV2: ColorScale = {
  50: FloralWhite,
  100: '#F4F1EA',
  200: '#E1DED8',
  300: '#C6C3BC',
  400: '#A3A09A',
  500: '#817E78',
  600: '#63605B',
  700: Charcoal,
  800: '#312E29',
  900: '#1A1712',
};

// --- Text --------------------------------------------------------------------

// The text group off charcoal and neutral. No marine in it: marine is a brand
// colour for surfaces, borders and the primary action, and a heading is not an
// action. Headings carry their weight through Lora italic and their size.
//
// Alphas of charcoal, because that is what Figma binds: text/secondary is
// #40404299 and text/disabled is #40404261, charcoal at 60% and 38%. This was
// solid ramp steps on the argument that a transparent token renders as two
// different colours over Floral White and over Gardenia, which is true and is
// now an accepted cost — Figma is the source of truth and it says alpha. The
// action group below was already alpha and already matched.
//
// Exported because the palette is not enough on its own. llama bakes a colour
// into each typography variant at import time (`color: themeColor.palette.text
// .title` in lib/llama/src/theme/mui/typography.ts), so `theme.typography.h1`
// carries llama's marine as a literal hex and MUI paints a heading from that,
// never looking at the palette. theme.tsx has to re-declare h1–h6 with these, or
// the headings stay marine no matter what `palette.text.title` says.
export const TextTitle = Charcoal;
// Charcoal at 77%. At 60% it blended to about 3.3:1 on Floral White and 3.1:1
// on Gardenia, under WCAG AA's 4.5:1 for body text. 77% is the lowest that
// passes on both surfaces (5.18:1 and 4.72:1).
export const TextSecondary = alpha(Charcoal, 0.77);
// Figma does not define a text/subtitle token. Held equal to secondary, which is
// what it was before the alphas went in, rather than invented separately.
export const TextSubtitle = alpha(Charcoal, 0.77);

// --- Collapsed families ----------------------------------------------------

export const LimeGreenV2 = flat(LimeGreenValue);
export const SunflowerV2 = flat(SunflowerValue);
export const WarmOrangeV2 = flat(WarmOrangeValue);
export const CherryV2 = flat(CherryValue);

// --- The MUI palette layer -------------------------------------------------

// The other half of the simplification: instead of a bespoke `surface-*`
// semantic layer, the new system uses MUI's own palette aliased to the
// primitives above. Transcribed from the Figma `palette` collection (Light
// mode). Dark mode is still MUI's blue/purple in Figma, so it is not mirrored.
//
// primary.main is `marine green/700` exactly as Figma binds it. Under the
// re-indexed ramp that resolves to #002825, near-black, rather than the brand
// #00433D it resolves to today. That is preserved here deliberately so the
// preview shows what the Figma file actually specifies — change the step to 500
// if the binding was meant to follow the brand green through the re-index.
export const previewPalette: ThemeOptions['palette'] = {
  primary: {
    main: MarineGreenV2[700],
    dark: MarineGreenV2[800],
    light: MarineGreenV2[400],
    contrastText: FloralWhite,
  },
  secondary: {
    main: CoralV2[500],
    dark: CoralV2[700],
    light: CoralV2[300],
    contrastText: FloralWhite,
  },
  text: {
    primary: Charcoal,
    secondary: TextSecondary,
    disabled: alpha(Charcoal, 0.38),
    title: TextTitle,
    subtitle: TextSubtitle,
  },
  divider: alpha(Charcoal, 0.12),
  background: {
    default: Gardenia,
    paper: FloralWhite,
  },
  action: {
    active: alpha(Charcoal, 0.56),
    hover: alpha(Charcoal, 0.04),
    selected: alpha(Charcoal, 0.08),
    focus: alpha(Charcoal, 0.12),
    disabled: alpha(Charcoal, 0.38),
    disabledBackground: alpha(Charcoal, 0.12),
  },
  custom: {
    coral: CoralV2,
    marineGreen: MarineGreenV2,
    neutral: NeutralV2,
    limeGreen: LimeGreenV2,
    sunflower: SunflowerV2,
    warmOrange: WarmOrangeV2,
    cherry: CherryV2,
    gardenia: Gardenia,
    gardeniaDark: GardeniaDark,
    charcoal: Charcoal,
    // No lavender. Deleted in the Figma file and gone from here too — the six
    // call sites that used it (status chips on the Promotions board and inspect
    // panel, the NorthStarClaims and benefits-flow tints, the LayersTree
    // feature marker) now read from the marine green ramp. The llama primitive
    // and its palette slot still exist upstream; see mui-augmentations.d.ts,
    // where the types drop it so nothing can reach for it by accident.
    //
    // Asserted, not satisfied: llama's PaletteOptions declares `custom` as the
    // full KareHeroColors, lavender included, and since TS 7 that declaration
    // wins over the Omit in mui-augmentations.d.ts. Filling the slot to please
    // the input type would undo the retirement, so the slot stays empty and the
    // gap is asserted here. theme.tsx deletes it from the live palette too.
    // Drop the assertion once llama itself retires lavender.
    //
    // Through unknown since gardeniaDark: a key llama's type does not have, as
    // well as one it requires and we leave out, and TS calls that too far
    // apart for a direct assertion.
  } as unknown as NonNullable<ThemeOptions['palette']>['custom'],
};
